import React, { useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  LinearProgress,
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Link,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSelector } from 'react-redux';
import { selectedUserIdSelector } from '../../../../selectors/userSelectors';
import {
  useGetMeetingInfoQuery,
  useUpdateMeetingInfoMutation,
  useSetAllowedAdditionalCallMutation
} from '../../../../api/taxProConsultationApi';
import moment from 'moment';
import { useDebounce } from 'react-use';
import { isTaxProSelector, isAdminSelector } from '../../../../selectors/authSelectors';
import { bookkeeperSelector } from '../../../../selectors/mainSelectors';
import _ from 'lodash';
import { captureException } from '@sentry/react';

const NotesSection = () => {
  const selectedUserId = useSelector(selectedUserIdSelector);
  const isAdmin = useSelector(isAdminSelector);
  const isTaxPro = useSelector(isTaxProSelector);
  const currentUser = useSelector(bookkeeperSelector);

  const { data: meetingData, error, isLoading } = useGetMeetingInfoQuery({ userId: selectedUserId });
  const hasUsedQuarterlyConsultation = meetingData?.hasUsedQuarterlyConsultation || false;

  const [updateMeetingInfo] = useUpdateMeetingInfoMutation();
  const [setAllowedAdditionalCall] = useSetAllowedAdditionalCallMutation();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const handleMenuClick = (event, meetingId) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMeetingId(meetingId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMeetingId(null);
  };

  if (error) {
    return <div>{error.message}</div>;
  }

  const renderCellContent = ({ value, includeLinks = false }) => {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
          {value}
        </Typography>
        {includeLinks && <LinksSection value={value} />}
      </Box>
    );
  };

  const renderMeetingInfo = (params) => {
    return (
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isTaxPro && !params.row.isNoShow && !params.row.isCancelled && (
            <IconButton
              aria-label='settings'
              size='small'
              onClick={(event) => handleMenuClick(event, params.row.meetingId)}
              sx={{ mr: 1, p: 0.5 }}>
              <MoreVertIcon fontSize='small' />
            </IconButton>
          )}
          <Typography variant='body2' sx={{ color: (params.row.isNoShow || params.row.isCancelled) && 'red' }}>
            {moment(params.row.meetingTime).format('M/D/YYYY, h:mm:ss A')} {params.row.isNoShow && '(No Show)'}
            {params.row.isCancelled && '(Cancelled)'}
          </Typography>
        </Box>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {params.row.taxProName}
        </Typography>
        {params.row.zoomLink && currentUser?.email === params.row.taxProEmail && (
          <Button
            variant='contained'
            size='small'
            href={params.row.zoomLink}
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              mt: 0.5,
              textTransform: 'none',
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark'
              }
            }}>
            Join Meeting
          </Button>
        )}
      </Box>
    );
  };

  const columns = [
    {
      field: 'meetingInfo',
      headerName: 'Meeting Info',
      width: 200,
      renderCell: renderMeetingInfo,
      sortable: true,
      sortComparator: (v1, v2, param1, param2) => {
        return new Date(param1.api.getRow(param1.id).meetingTime) - new Date(param2.api.getRow(param2.id).meetingTime);
      }
    },
    {
      field: 'clientNotes',
      headerName: 'Client Notes',
      flex: 0.4,
      renderCell: (params) => renderCellContent({ value: params.value, includeLinks: false })
    },
    {
      field: 'taxProNotes',
      headerName: 'Tax Pro Notes',
      flex: 0.6,
      renderCell: (params) =>
        // Notes only editable by tax pro. If not, render readonly content
        isTaxPro || isAdmin ? (
          <TaxProNotesCell
            value={params.value}
            onChange={(updatedNote) => {
              updateMeetingInfo({ meetingId: params.row.meetingId, taxProNotes: updatedNote });
            }}
          />
        ) : (
          renderCellContent({ value: params.value, includeLinks: true })
        )
    }
  ];

  const handleConfirmReset = async () => {
    try {
      await setAllowedAdditionalCall({ userId: selectedUserId, isAllowedAdditionalCall: true }).unwrap();
      setIsResetModalOpen(false);
    } catch (err) {
      captureException(err);
    }
  };

  return (
    <Paper data-testid='notes-section' elevation={0} sx={{ height: '100%', backgroundColor: 'transparent' }}>
      {hasUsedQuarterlyConsultation && (
        <Button
          variant='contained'
          size='small'
          onClick={() => setIsResetModalOpen(true)}
          sx={{ textTransform: 'none', mb: 1 }}>
          Reset Quarterly Consultation
        </Button>
      )}
      <DataGrid
        rows={_.defaultTo(meetingData?.meetings, [])}
        columns={columns}
        slots={{
          loadingOverlay: LinearProgress
        }}
        loading={isLoading}
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: 'meetingInfo', sort: 'desc' }]
          }
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: 'normal',
            padding: '0px'
          }
        }}
        getRowId={(row) => row.meetingId}
      />
      <TaxProNotesMenu menuAnchorEl={menuAnchorEl} onClose={handleMenuClose} selectedMeetingId={selectedMeetingId} />
      <ResetCallConfirmationModal
        open={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onConfirm={handleConfirmReset}
      />
    </Paper>
  );
};

const TaxProNotesMenu = ({ menuAnchorEl, onClose, selectedMeetingId }) => {
  const [updateMeetingInfo] = useUpdateMeetingInfoMutation();

  const handleMarkNoShow = () => {
    updateMeetingInfo({ meetingId: selectedMeetingId, isNoShow: true });
    onClose();
  };

  const isMenuOpen = !_.isNil(menuAnchorEl);
  return (
    <Menu
      anchorEl={menuAnchorEl}
      open={isMenuOpen}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
      <MenuItem onClick={handleMarkNoShow}>Mark as no-show</MenuItem>
    </Menu>
  );
};

const TaxProNotesCell = ({ value, onChange }) => {
  const [note, setNote] = useState(value);

  useDebounce(
    () => {
      onChange(note);
    },
    1000,
    [note]
  );

  const updateNote = (event) => {
    setNote(event.target.value);
  };

  const handleKeyDown = (event) => {
    // Prevent DataGrid from capturing spacebar presses, allowing spaces in TextField
    if (event.key === ' ') {
      event.stopPropagation();
    }
  };

  return (
    <>
      <TextField
        multiline
        fullWidth
        minRows={4}
        value={note}
        onChange={updateNote}
        onKeyDown={handleKeyDown}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            padding: 0,
            backgroundColor: 'white'
          },
          '& .MuiInputBase-inputMultiline': {
            padding: '8px 10px 32px 10px',
            whiteSpace: 'pre-wrap'
          }
        }}
      />
      <LinksSection value={note} />
    </>
  );
};

const LinksSection = ({ value }) => {
  const links = useMemo(() => {
    const regex = /\bhttps?:\/\/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?\b/g;
    return value.match(regex) || [];
  }, [value]);

  if (_.isEmpty(links)) {
    return null;
  }

  return (
    <>
      <Typography
        variant='body1'
        sx={{
          mt: 2
        }}>
        Links:
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {links.map((link, index) => (
          <Link key={index} href={link} target='_blank' rel='noopener noreferrer'>
            {link}
          </Link>
        ))}
      </Stack>
    </>
  );
};

const ResetCallConfirmationModal = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>Are you sure?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: 'red' }}>
          Resetting the call will allow the user to book additional calls without payment!
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-around' }}>
        <Button onClick={onConfirm} variant='contained' color='primary' sx={{ width: '100%' }}>
          Yes
        </Button>
        <Button onClick={onClose} variant='outlined' sx={{ width: '100%' }}>
          No
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotesSection;
